import { Utils } from '../../../common/misc/Utils';
import CustomSharedUtils from '../../../custom_shared/misc/Utils';
import * as React from 'react';
import {
    FormInputFieldData,
    FormInputParams,
    InputOverrideCondition,
    WidgetData,
    WidgetDataItem
} from '../../../common/services/types';
import { Col, DatePicker, FormInstance, Input, InputNumber, Select, Tooltip } from 'antd';
import FormItem from 'antd/lib/form/FormItem';
import { DynamicUiModel } from '../../../common/stores/DynamicUiModel';
import { DynamicModalForm } from './DynamicModalForm';
import { Subject } from 'rxjs';
import { useEffect } from 'react';
import { reaction } from 'mobx';
import { QuestionCircleOutlined } from '@ant-design/icons';

type RenderEditProps = {
    id: string;
    selectedRecord: object | undefined;
    inputParams: FormInputParams;
    recordEditForm: FormInstance;
    sources: { [key: string]: WidgetData };
    ui: DynamicUiModel;
    onHighlightBlock: (field: FormInputFieldData, inputId: string) => void;
    getGearIcon: (inputParams: FormInputParams) => React.ReactElement;
    highlightInputSubject?: Subject<string>;
    formVersion?: number;
    // tslint:disable-next-line:no-any
    onAction: (id: string, action: string, value?: unknown) => void;
};

// eslint-disable-next-line max-len
export const DynamicVisualTableModal: React.FC<RenderEditProps> = ({
    ui,
    id,
    selectedRecord,
    inputParams,
    recordEditForm,
    sources,
    onHighlightBlock,
    highlightInputSubject,
    getGearIcon,
    formVersion,
    onAction
}) => {
    const selectedRecordMap = selectedRecord as Record<string, { value?: unknown }> | undefined;
    const [source, setSource] = React.useState<{ [key: string]: WidgetData }>(sources);
    const isInitialSourceRefresh = React.useRef(true);
    const formattedKey = Utils.insertWhitespaceBeforeCapital(id).replace(' ', '');
    const override =
        inputParams.behavior &&
        inputParams.behavior.inputOverride &&
        inputParams.behavior.inputOverride.find(i => i.key === id || i.key === formattedKey);

    const normalizeConditionValue = React.useCallback((value: unknown) => {
        if (Array.isArray(value)) {
            return value.map(x => String(x ?? '').trim());
        }

        return [String(value ?? '').trim()];
    }, []);

    const getConditionFieldValues = React.useCallback(
        (field: string) => {
            const formValue = recordEditForm.getFieldValue(field);
            const selectedRecordValue = selectedRecordMap?.[field]?.value;
            const hasFormValue = formValue !== undefined || recordEditForm.isFieldTouched(field);
            return normalizeConditionValue(hasFormValue ? formValue : selectedRecordValue);
        },
        [normalizeConditionValue, recordEditForm, selectedRecordMap]
    );

    const matchesCondition = React.useCallback(
        (condition: InputOverrideCondition | undefined) => {
            if (!condition?.field || !condition.values?.length) {
                return false;
            }

            const conditionValues = getConditionFieldValues(condition.field);

            return conditionValues.some(value =>
                condition.values.some(conditionValue => conditionValue.trim().toLowerCase() === value.toLowerCase())
            );
        },
        [formVersion, getConditionFieldValues]
    );

    const isEmpty = override ? matchesCondition(override.emptyWhen) : false;
    const isDisabled = override ? matchesCondition(override.disabledWhen) : false;
    const hasTooltipCondition = !!override?.disabledWhen || !!override?.emptyWhen;
    const showInputTooltip = !!override?.inputTooltip && (!hasTooltipCondition || isDisabled || isEmpty);

    useEffect(() => {
        const inputOverride = inputParams.behavior?.inputOverride;

        const parentInputId = inputOverride ? inputOverride.find(x => x.key === id)?.parentInputId : null;

        const disposer =
            (parentInputId &&
                reaction(
                    () => ui.parentBasedInputValues[parentInputId!],
                    (items: WidgetDataItem[]) => {
                        if (override) {
                            const val = override.source;
                            const sourceObj: Record<string, WidgetData> = {};
                            Object.assign(sourceObj, source);
                            sourceObj[val] = { total: items.length, skip: 0, data: items };
                            setSource(sourceObj);
                        }

                        const currentValue = recordEditForm.getFieldValue(id);
                        // The refresh while opening a row may retain its persisted value.
                        // Later parent updates keep the existing clear behavior.
                        const shouldPreserveInitialValue =
                            isInitialSourceRefresh.current &&
                            recordEditForm.getFieldValue(parentInputId) === selectedRecordMap?.[parentInputId]?.value &&
                            currentValue !== undefined &&
                            items.some(item => item.value === currentValue);

                        isInitialSourceRefresh.current = false;

                        if (!shouldPreserveInitialValue) {
                            recordEditForm.setFieldsValue({ [id]: undefined });
                        }
                    }
                )) ||
            null;
        return () => {
            if (disposer) {
                disposer();
            }
        };
    }, [id, inputParams.behavior?.inputOverride, override, recordEditForm, source, ui.parentBasedInputValues]);

    useEffect(() => {
        loadSource();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSource = () => {
        const inputOverride = inputParams.behavior?.inputOverride;

        const parentInputId = inputOverride ? inputOverride.find(x => x.key === id)?.parentInputId : null;

        if (!parentInputId || !selectedRecord) {
            return;
        }

        const parentValue = selectedRecordMap?.[parentInputId]?.value;

        const changeObj: Record<string, unknown> = {};
        changeObj[parentInputId] = parentValue;

        ui.setCurrentFieldsChange(changeObj);
    };

    const getFieldName = (key: string) => {
        const displayColumn =
            inputParams.behavior &&
            inputParams.behavior.displayColumns &&
            inputParams.behavior.displayColumns.find(dc => dc.key === key)
                ? inputParams.behavior.displayColumns.find(dc => dc.key === key)
                : undefined;

        if (displayColumn) {
            return displayColumn.header;
        } else {
            const inputs = inputParams?.behavior?.inputOverride;
            const name = inputs && inputs.find(x => x.key === key)?.name;
            return name ? name : Utils.insertWhitespaceBeforeCapital(key);
        }
    };

    const getModalFormControl = (params: FormInputParams, key: string) => {
        if (!params.modalForm || !ui.actions || !ui.actions.length) {
            return null;
        }

        const newInputParams = {
            ...params,
            id: key
        };

        return (
            <DynamicModalForm
                inputParams={newInputParams}
                form={recordEditForm}
                getGearIcon={getGearIcon}
                onAction={onAction}
                onHighlightBlock={onHighlightBlock}
                ui={ui}
                highlightInputSubject={highlightInputSubject}
            />
        );
    };

    const getInputControls = (key: string) => {
        if (inputParams.behavior && inputParams.behavior.inputOverride) {
            const formattedId = Utils.insertWhitespaceBeforeCapital(id).replace(' ', '');
            const overrideInput = inputParams.behavior.inputOverride.find(i => i.key === key || i.key === formattedId);

            if (overrideInput && overrideInput.hasModalForm === true) {
                return getModalFormControl(inputParams, key);
            }
        }
        return null;
    };

    if (!selectedRecord) {
        return null;
    }

    const getCurrentWidgetValue = () => {
        if (override?.type == 'dropdown' && override.source && source[override.source] && selectedRecordMap) {
            return (
                source[override.source].data.find(x => x.text === selectedRecordMap[id]?.value)?.value ||
                selectedRecordMap[id]?.value
            );
        } else {
            return selectedRecordMap?.[id]?.value;
        }
    };

    let formObj: Record<string, unknown> = {};

    const getSystemInputValue = () => {
        const inputs = inputParams?.behavior?.systemInputs;
        const sysInput = inputs && inputs.find(x => x.key === id);
        return sysInput && sysInput.value;
    };

    const currentFormValue = recordEditForm.getFieldValue(id);
    const hasCurrentFormValue = currentFormValue !== undefined || recordEditForm.isFieldTouched(id);
    let shouldSetFieldValue = true;

    if (isEmpty) {
        formObj[id] = undefined;
        if (selectedRecordMap?.[id]) {
            selectedRecordMap[id].value = undefined;
        }
    } else if (hasCurrentFormValue) {
        shouldSetFieldValue = false;
    } else if (override && override.type === 'datepicker') {
        formObj[id] = CustomSharedUtils.convertDateValueToPickerValue(selectedRecordMap?.[id]?.value);
    } else {
        formObj[id] = selectedRecordMap?.[id] ? getCurrentWidgetValue() : getSystemInputValue();
    }

    if (shouldSetFieldValue) {
        recordEditForm.setFieldsValue(formObj);
    }

    let input = (
        <Input
            className="dynamic-input"
            value={isEmpty ? '' : selectedRecordMap?.[id] ? String(selectedRecordMap[id]?.value ?? '') : ''}
            maxLength={override?.maxLength ?? undefined}
            disabled={isDisabled}
            // onChange={(e) => updateFieldInSelectedRecord(key, e.target.value) }
        />
    );
    let span = 10;
    let offset = 1;

    if (override) {
        if (override.hasRowSpecificSource && inputParams.behavior?.displayColumns) {
            const overrideKeyColumn =
                override.rowSpecificSourceKey ??
                inputParams.behavior.displayColumns.find(dc => dc.key == override.key)?.rowSpecificSourceKey ??
                '';
            const sourceId = selectedRecordMap?.[overrideKeyColumn]?.value;
            const inputSource = ui.uiModel.sources.find(s => s.id === `${override.key}-${sourceId}`);

            input = (
                <Select
                    allowClear
                    className="dynamic-input"
                    showSearch
                    disabled={isDisabled}
                    options={
                        !inputSource
                            ? []
                            : inputSource.data.map(i => {
                                  return { label: i.text, value: i.value };
                              })
                    }
                    mode={override.multiSelect === true ? 'tags' : undefined}
                />
            );

            if (!inputSource) {
                const recordFormObj: Record<string, unknown> = {};
                recordFormObj[id] = undefined;
                recordEditForm.setFieldsValue(recordFormObj);
            }
        }

        switch (override.type) {
            case 'datepicker':
                input = (
                    <DatePicker
                        className="dynamic-input"
                        disabled={isDisabled}
                        // onChange={(val) => updateDateInSelectedRecord(key, val)}
                    />
                );
                break;
            case 'dropdown':
                if (override.source && source[override.source]) {
                    const style: React.CSSProperties | undefined = override.singleInputPerRow
                        ? { width: 'calc(50% - 14px)' }
                        : undefined;

                    input = (
                        <Select
                            className="dynamic-input"
                            showSearch
                            optionFilterProp="label"
                            options={source[override.source].data.map(i => {
                                return { label: i.text, value: i.value };
                            })}
                            mode={override.multiSelect === true ? 'tags' : undefined}
                            style={style}
                            allowClear
                            disabled={isDisabled}
                            // onSelect={(val) => updateFieldInSelectedRecord(key, val as string)}
                        />
                    );
                }
                break;
            case 'text':
                input = (
                    <Input.TextArea
                        className="dynamic-input"
                        rows={7}
                        autoSize={{ maxRows: 7 }}
                        maxLength={override?.maxLength ?? undefined}
                        disabled={isDisabled}
                    />
                );
                break;
            case 'number':
                input = (
                    <InputNumber
                        className="dynamic-input"
                        precision={override.numberPrecision ?? 0}
                        style={{ width: '100%' }}
                        decimalSeparator="."
                        disabled={isDisabled}
                    />
                );
                break;
            default:
                break;
        }

        if (override.width === 'wide' || override.singleInputPerRow === true) {
            span = 21;
            offset = 1;
        }
    }

    const label = (
        <div className="input-label">
            <span className="label-title">{getFieldName(id)}</span>
            {showInputTooltip && (
                <Tooltip overlayClassName="alpha-input-tooltip" title={override.inputTooltip} placement="top">
                    <QuestionCircleOutlined style={{ marginLeft: 6, verticalAlign: 'middle' }} />
                </Tooltip>
            )}
            {getInputControls(id)}
        </div>
    );

    const handleDateChange = (dateObj: unknown) => {
        if (dateObj) {
            return dateObj;
        }

        return undefined;
    };

    return (
        <Col key={id} span={span} offset={offset}>
            <div className="input-wrapper">
                <FormItem
                    colon={false}
                    label={label}
                    name={id}
                    getValueFromEvent={override && override.type == 'datepicker' ? handleDateChange : undefined}
                >
                    {input}
                </FormItem>
            </div>
        </Col>
    );
};
